import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { saveProject } from '~/lib/.client/api/projects';
import { forkProject } from '~/lib/projects/forkProject';
import { projectStore } from '~/lib/stores/project';
import { workbenchStore } from '~/lib/stores/workbench';
import { invariant } from '~/utils/invariant';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('useProjects');

export function useProjects() {
  const [conflict, setConflict] = useState(false);
  const [conflictData, setConflictData] = useState<{ lastEditor: string; editedAt: string } | undefined>();

  useEffect(() => {
    const disposeConflictListener = projectStore.registerListener('conflict', (lastEditor, editedAt) => {
      setConflictData({ lastEditor, editedAt });
      setConflict(true);
    });

    const disposeForkListener = projectStore.registerListener('fork', onForkProject);

    const disposeProjectTooBigListener = projectStore.registerListener('project-too-big', () => {
      const slug = projectStore.getSlug();

      invariant(slug);

      toast.error(
        "Your project is getting too big to save. We are working hard on supporting bigger projects. You can download your project by clicking the 'Download' button on top so you don't lose any changes.",
      );
    });

    return () => {
      disposeForkListener();
      disposeConflictListener();
      disposeProjectTooBigListener();
    };
  }, []);

  const onForkProject = useCallback(async () => {
    try {
      await forkProject();

      setConflict(false);
    } catch (error) {
      logger.error(error);

      toast.error('Issues connecting to Bolt');

      throw new Error('Failed to fork project');
    }
  }, []);

  const onOverwriteProject = useCallback(async () => {
    try {
      const slug = projectStore.getSlug();
      const editedAt = projectStore.getEditedAt();

      invariant(slug);
      invariant(editedAt);

      const response = await saveProject({
        slug,
        editedAt,
        files: workbenchStore.files.get(),
        force: true,
      });

      if (!response) {
        return;
      }

      projectStore.setEditedAt(response.editedAt);

      setConflict(false);
    } catch (error) {
      logger.error(error);

      toast.error('Issues connecting to Bolt');

      throw new Error('Failed to overwrite project');
    }
  }, []);

  return {
    onForkProject,
    onOverwriteProject,
    conflict,
    conflictData,
  };
}
